import { useBulkApply } from '@hooks/features/useBulkApply';
import { Button } from '@components/common';
import { useTranslation } from 'next-i18next';
import { Input } from '@components/common/Forms';
import { CardJobTalentMemo } from '@components/templates/job/Card/CardJobTalent';

// Interface
// ******************************************
interface BulkApplyJobListProps {
  jobList: ApiSchema<'JobItem'>[];
  locale: Locale;
  onBulkApplySuccess?: () => void;
  renderExtraContent?: () => React.ReactNode;
}

/**
 * @description Bulk apply job list component. Renders a job list for bulk apply, with a select / deselect all checkbox
 * and a button to trigger the bulk apply action.
 *
 * - **jobList** - ApiSchema<'JobItem'>[] - An array of recommended jobs for bulk apply.
 * - **locale** - Locale - The current app locale.
 * - **onBulkApplySuccess** - function, optional - A callback function that is executed after the bulk apply action is successfully completed.
 */
export const BulkApplyJobList = (props: BulkApplyJobListProps) => {
  // Destructure props
  const {
    jobList, locale, onBulkApplySuccess, renderExtraContent,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Bulk apply
  // ***********************************************
  const {
    record,
    formGroupName,
    handleStep,
    formRef,
    selectAll,
    setSelectAll,
    bulkApplyAction,
    isMutating,
  } = useBulkApply(() => {
    if (onBulkApplySuccess) {
      onBulkApplySuccess();
    }
  });

  // Render component
  // *****************************************************
  return (
    <>
      <div>
        <div className="block items-center justify-between md:flex mb-10">
          <div className="mb-3 mr-5 flex flex-col md:mb-0">
            {renderExtraContent ? renderExtraContent() : <p className="text-sm">{t('bulk.apply.text.description')}</p>}
          </div>
          <div className="flex items-center justify-between md:justify-start ">
            <Button
              color="secondary"
              size="sm"
              rounding="full"
              className="mr-2"
              isLoading={isMutating}
              onClick={bulkApplyAction}
            >
              <span>{t('bulk.apply.text.button')}</span>
            </Button>

            {/* Select / deselect all jobs. If the user unchecks a job when selectedAll is true, selectedAll will become 'undefined' */}
            <label
              htmlFor="selectAll"
              title={t('global.action.toggle_select')}
              aria-label={t('global.action.toggle_select')}
              className="ml-4 flex cursor-pointer items-center p-1"
            >
              <Input
                name="selectAll"
                type="checkbox"
                className="flex size-6 cursor-pointer text-secondary checked:bg-secondary"
                checked={!!selectAll}
                onClick={() => setSelectAll(!selectAll)}
              />
            </label>

          </div>
        </div>
      </div>

      <form noValidate onSubmit={handleStep} ref={formRef}>
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
          {jobList.map((job) => (
            <CardJobTalentMemo
              key={job.id}
              job={job}
              bulkApply={{
                record,
                name: formGroupName,
                setSelectAll,
              }}
              locale={locale}
            />
          ))}
        </div>
      </form>
    </>
  );
};
